import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { ChevronRight, ArrowRight, Phone, Flame, RotateCcw, Wrench, Zap, Info } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

/**
 * Hot Water: Repair or Replace — simple 4-question decision tree.
 * Each answer carries a weight. We score at the end: lower score = repair, higher = replace.
 */

const QUESTIONS = [
  {
    id: "age",
    title: "How old is the unit?",
    options: [
      { label: "Under 5 years", weight: 0 },
      { label: "5 – 8 years", weight: 1 },
      { label: "8 – 12 years", weight: 3 },
      { label: "12+ years / unknown", weight: 5 },
    ],
  },
  {
    id: "fault",
    title: "What's happening?",
    options: [
      { label: "Pilot keeps going out", weight: 1 },
      { label: "No hot water at all", weight: 2 },
      { label: "Not hot enough / runs out fast", weight: 2 },
      { label: "Leaking from the tank", weight: 5 },
      { label: "Water looks rusty / brown", weight: 4 },
      { label: "Loud banging / rumbling noise", weight: 3 },
    ],
  },
  {
    id: "type",
    title: "What type of system?",
    options: [
      { label: "Electric storage", weight: 1 },
      { label: "Gas storage", weight: 1 },
      { label: "Gas continuous flow", weight: 0 },
      { label: "Solar hot water", weight: 2 },
      { label: "Heat pump", weight: 1 },
    ],
  },
  {
    id: "cost",
    title: "What are your running costs like?",
    options: [
      { label: "Fine — happy as it is", weight: 0 },
      { label: "A bit high but tolerable", weight: 1 },
      { label: "Painful — looking to cut it", weight: 3 },
    ],
  },
];

const verdictFor = (score) => {
  if (score <= 3) {
    return {
      key: "repair",
      title: "Repair",
      color: "text-emerald-400",
      border: "border-emerald-500",
      bg: "bg-emerald-500/[0.04]",
      icon: Wrench,
      body: "Your unit is young and the fault sounds like a repairable component (element, thermostat, valve, pilot assembly). Expect $150–$450 for most repairs, back online same day in many cases.",
      cta: "Book a service call",
      ctaIcon: Phone,
    };
  }
  if (score <= 7) {
    return {
      key: "either",
      title: "Could go either way",
      color: "text-gold",
      border: "border-gold/40",
      bg: "bg-gold/[0.04]",
      icon: Zap,
      body: "Age and fault put you in the grey zone. A repair might get you another 2–3 years, but the next failure usually isn't far behind. Worth getting a diagnosis and a replacement quote side-by-side — we lay both numbers out including rebates and running cost differences.",
      cta: "Get both quotes",
      ctaIcon: Phone,
    };
  }
  return {
    key: "replace",
    title: "Replace",
    color: "text-emergency",
    border: "border-emergency",
    bg: "bg-emergency/[0.04]",
    icon: Flame,
    body: "Age, fault, and/or running cost point to replacement being the better move. A new heat pump cuts running costs by up to 70% vs electric, and Victorian + Federal rebates stack to drop the upfront cost significantly. See the Rebate Finder for a ballpark.",
    cta: "See rebates",
    ctaIcon: Zap,
    secondaryHref: "/tools/rebate-finder",
    secondaryLabel: "Open Rebate Finder",
  };
};

const HotWaterDecision = () => {
  const [idx, setIdx] = useState(0);
  const [picks, setPicks] = useState({});
  const [result, setResult] = useState(null);

  const q = QUESTIONS[idx];
  const total = QUESTIONS.length;

  const pick = (opt) => {
    const next = { ...picks, [q.id]: opt };
    setPicks(next);
    if (idx + 1 < total) {
      setIdx(idx + 1);
    } else {
      const score = Object.values(next).reduce((s, p) => s + p.weight, 0);
      setResult(verdictFor(score));
      setTimeout(() => {
        document.getElementById("hw-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  const reset = () => {
    setIdx(0);
    setPicks({});
    setResult(null);
  };

  const back = () => {
    if (idx > 0) {
      setIdx(idx - 1);
      const next = { ...picks };
      delete next[QUESTIONS[idx - 1].id];
      setPicks(next);
    }
  };

  return (
    <div data-testid="hw-decision-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-16">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <Link to="/tools" className="text-muted hover:text-gold">Tools</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Repair or Replace</span>
          </nav>
          <div className="max-w-3xl">
            <div className="eyebrow mb-6"><span>Free Tool · 4 Questions</span></div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink"
            >
              Hot water unit
              <br />
              <span className="text-gold">worth saving?</span>
            </motion.h1>
            <p className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed">
              Four quick questions. We&apos;ll give you an honest call: repair, replace, or get
              both numbers side-by-side.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          {!result && (
            <>
              <div className="flex gap-2 mb-10 max-w-xl">
                {QUESTIONS.map((_, i) => (
                  <div key={i} className={`h-[3px] flex-1 transition-colors duration-500 ${i <= idx ? "bg-gold" : "bg-line"}`} />
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.3 }}
                  className="bg-card border border-line p-8 md:p-12 max-w-3xl"
                  data-testid={`hw-question-${idx}`}
                >
                  <div className="text-gold text-xs tracking-widerx uppercase mb-3">Question {idx + 1} of {total}</div>
                  <h2 className="font-display text-3xl md:text-4xl text-ink mb-8">{q.title}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {q.options.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => pick(opt)}
                        data-testid={`hw-answer-${idx}-${i}`}
                        className="group text-left p-5 border border-line hover:border-gold hover:bg-cardHover transition-colors"
                      >
                        <div className="font-display text-lg text-ink group-hover:text-gold transition-colors">{opt.label}</div>
                      </button>
                    ))}
                  </div>
                  {idx > 0 && (
                    <button
                      onClick={back}
                      data-testid="hw-back"
                      className="mt-8 btn-ghost"
                    >
                      ← Back
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
            </>
          )}

          {result && (
            <motion.div
              id="hw-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`p-8 md:p-12 border-l-4 ${result.border} ${result.bg} max-w-3xl`}
              data-testid="hw-result"
            >
              <div className="flex items-center gap-3 mb-4">
                <result.icon size={28} className={result.color} />
                <span className={`font-display text-3xl md:text-4xl uppercase ${result.color}`}>
                  Verdict: {result.title}
                </span>
              </div>
              <p className="mt-6 text-muted text-lg leading-relaxed max-w-2xl">{result.body}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={PHONE_HREF} className="btn-gold" data-testid="hw-cta-call">
                  <result.ctaIcon size={16} /> {result.cta}
                </a>
                {result.secondaryHref && (
                  <Link to={result.secondaryHref} className="btn-ghost" data-testid="hw-cta-secondary">
                    {result.secondaryLabel} <ArrowRight size={16} />
                  </Link>
                )}
                <button onClick={reset} className="btn-ghost" data-testid="hw-reset">
                  <RotateCcw size={14} /> Start again
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-16 bg-elevated border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-14 max-w-3xl">
          <div className="p-6 border-l-2 border-gold/40 bg-gold/[0.03] flex items-start gap-3">
            <Info size={18} className="text-gold mt-0.5 flex-shrink-0" />
            <p className="text-muted text-sm leading-relaxed">
              This is a guide, not a diagnosis. Hot water decisions are a mix of fault type,
              age, running cost and what&apos;s available in stock locally. Rules of thumb:
              under 8 years with a minor fault — repair. Over 10 years with a major fault —
              replace. Everything in between: get a proper look at it.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HotWaterDecision;
